import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../../api/client";
import type { PricingPlan } from "../../api/hooks";
import { LoadingSection } from "../../components/Spinner";

export function AdminPricing() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "pricing"],
    queryFn: () => api<PricingPlan[]>("/admin/pricing"),
  });
  const [editing, setEditing] = useState<PricingPlan | null>(null);

  const save = useMutation({
    mutationFn: (p: PricingPlan) => {
      const body = {
        category: p.category,
        name: p.name,
        priceCents: p.priceCents,
        period: p.period,
        highlight: p.highlight,
        description: p.description,
        order: p.order,
      };
      return p.id
        ? api(`/admin/pricing/${p.id}`, { method: "PUT", json: body })
        : api("/admin/pricing", { method: "POST", json: body });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
      setEditing(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/pricing/${id}`, { method: "DELETE" }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ["admin", "pricing"] });
      qc.invalidateQueries({ queryKey: ["pricing"] });
      if (editing?.id === id) setEditing(null);
    },
  });

  const handleDelete = (plan: PricingPlan) => {
    const priceLabel =
      plan.priceCents != null ? ` (${(plan.priceCents / 100).toFixed(2)} €)` : "";
    const ok = window.confirm(`Supprimer la formule « ${plan.name} »${priceLabel} ?`);
    if (ok) remove.mutate(plan.id);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-bold uppercase">Tarifs</h1>
        <button
          onClick={() =>
            setEditing({
              id: "",
              category: "sans_engagement",
              name: "",
              priceCents: null,
              period: null,
              highlight: false,
              description: null,
              order: 0,
            })
          }
          className="btn-primary"
        >
          + Ajouter
        </button>
      </div>

      {isLoading && <LoadingSection />}

      <table className="mt-6 w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-ink-500">
            <th className="py-2">Nom</th>
            <th>Catégorie</th>
            <th>Prix</th>
            <th>Période</th>
            <th></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {data?.map((p) => (
            <tr key={p.id}>
              <td className="py-2 font-semibold">{p.name}</td>
              <td>{p.category}</td>
              <td>{p.priceCents != null ? `${(p.priceCents / 100).toFixed(2)} €` : "—"}</td>
              <td>{p.period ?? "—"}</td>
              <td className="text-right">
                <button onClick={() => setEditing(p)} className="text-xs underline">
                  Éditer
                </button>
                <button
                  onClick={() => handleDelete(p)}
                  className="ml-3 text-xs text-brand-500 underline"
                >
                  Suppr.
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-[80vw] overflow-y-auto rounded-lg bg-white p-6">
            <h2 className="font-display text-xl font-bold">
              {editing.id ? "Éditer" : "Nouvelle formule"}
            </h2>
            <div className="mt-4 grid gap-3">
              <input
                className="input"
                placeholder="Nom"
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <select
                className="input"
                value={editing.category}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
              >
                <option value="engagement">Avec engagement</option>
                <option value="sans_engagement">Sans engagement</option>
                <option value="kids">Kids</option>
                <option value="sur_devis">Sur devis</option>
              </select>
              <input
                className="input"
                type="number"
                placeholder="Prix en centimes (ex: 7900)"
                value={editing.priceCents ?? ""}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    priceCents: e.target.value ? Number(e.target.value) : null,
                  })
                }
              />
              <input
                className="input"
                placeholder="Période (mois, séance…)"
                value={editing.period ?? ""}
                onChange={(e) => setEditing({ ...editing, period: e.target.value || null })}
              />
              <textarea
                className="input"
                rows={3}
                placeholder="Description"
                value={editing.description ?? ""}
                onChange={(e) =>
                  setEditing({ ...editing, description: e.target.value || null })
                }
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={editing.highlight}
                  onChange={(e) => setEditing({ ...editing, highlight: e.target.checked })}
                />
                Mettre en avant
              </label>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={() => save.mutate(editing)} className="btn-primary">
                Enregistrer
              </button>
              <button onClick={() => setEditing(null)} className="btn-ghost">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
